import React, { useEffect, useRef, useState } from "react";
import Tabs from "../../../../../../Tabs";
import { Switch, Tooltip } from "@mui/material";
import TextView from "../../../../../../TextView";
import { colors } from "../../../../../../../utils";
import ColorPopup from "../../../../../Shared/ColorPopup";
import SettingsSlider from "../../../../../Shared/SettingsSlider";
import FroalaTextEditor from "../../../../../../FroalaEditor/children/FroalaTextEditor";
import { useDispatch, useSelector } from "react-redux";
import { updateButtonProps } from "../../../../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import Input from "../../../../../../Input";
import ShareWithBorder from "../../../../../../../assets/icons/ShareWithBorder";
import { goToUrl } from "../../../../../../../utils/helpers";
import { isSafeHttpUrl } from "../../../../../../../utils/urlValidation";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// The link field is a rich-text editor; strip its markup to the raw URL text
// before validating / opening.
const stripHtml = (v = "") => String(v).replace(/<\/?[^>]+(>|$)/g, "").trim();

function ButtonsDesign({ allButtons, handleChangeButtonsList }) {
  const mainTabs = ["Button 1", "Button 2", "Button 3"];
  const [selectedMainTab, setSelectedMainTab] = useState(mainTabs[0]);

  const handleToggleActive = (id) => {
    setList((prevList) => {
      const activeItemsCount = prevList.filter((item) => item.isActive).length;
      return prevList.map((item) => {
        if (item.id === id) {
          // If toggling this item off and it's the only active one, prevent deactivation
          if (item.isActive && activeItemsCount === 1) {
            setTooltipVisible(true);
            return item;
          }
          setTooltipVisible(false);
          return { ...item, isActive: !item.isActive };
        }
        return item;
      });
    });
  };

  useEffect(() => {
    const tabToIndexMap = mainTabs.reduce((acc, tab, index) => {
      acc[tab] = index;
      return acc;
    }, {});

    const tempItem = allButtons[tabToIndexMap[selectedMainTab]];
    setItem(tempItem);
  }, [selectedMainTab, allButtons]);

  const initialData = allButtons.map((button) => ({
    ...button,
    isOpen: false, // Add isOpen state to control accordion expansion
  }));

  const [list, setList] = useState(initialData);
  const [item, setItem] = useState(allButtons[0]);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const handleTitleChange = (id, value) => {
    if (editorRef1.current && editorRef1.current?.events?.focus) {
      const buttonTitles = ["Button 1", "Button 2", "Button 3"];

      setList((prevList) =>
        prevList.map((item) => {
          if (item.id === id && item.isActive) {
            // Remove "Button 1", "Button 2", or "Button 3" from the value
            let updatedTitle = value.replace(/<[^>]*>/g, "").trim(); // Remove HTML tags and trim
            buttonTitles.forEach((title) => {
              if (updatedTitle.startsWith(title)) {
                updatedTitle = updatedTitle.replace(title, "").trim(); // Remove the title and trim
              }
            });

            return {
              ...item,
              title: value, // Set the cleaned value as the title
            };
          }
          return item;
        })
      );
    }
  };

  const handleLinkChange = (id, value) => {
    setList((prevList) =>
      prevList.map((item) => (item.id === id ? { ...item, link: value } : item))
    );
  };

  useEffect(() => {
    handleChangeButtonsList(list);
  }, [list]);

  const editorRef1 = useRef(null);
  const editorRef2 = useRef(null);

  const dispatch = useDispatch();
  const { buttonProps } = useSelector((state) => state.codeblockStyles.button);

  // Button link validation (nemesis #151): flag a non-empty link that is not a
  // safe http(s) URL, and block opening it.
  const rawLink = stripHtml(item?.link);
  const linkError = rawLink ? !isSafeHttpUrl(rawLink) : false;

  return (
    <div>
      <Tabs
        categories={mainTabs}
        selectedCategory={selectedMainTab}
        onCategoryChange={(_, val) => {
          setSelectedMainTab(val);
          setTooltipVisible(false);
        }}
        tabStyle={{ maxWidth: "50%" }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          margin: "20px",
          marginTop: "20px",
          paddingBottom: "15px",
          marginBottom: "-4px",
          borderBottom: "0.5px solid #e6e0d1",
        }}
      >
        <TextView
          style={{ fontSize: "16px", fontWeight: 500, color: "#565556" }}
        >
          Display Button
        </TextView>
        <Tooltip
          title="At least 1 Button must be enabled"
          open={tooltipVisible}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          <Switch
            checked={item.isActive}
            onChange={() => handleToggleActive(item.id)}
            inputProps={{ "aria-label": "controlled" }}
            size="small"
            sx={{
              marginLeft: "5px",
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#81459B", // Custom color for the checked switch
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#81459B", // Custom color for the track
              },
            }}
          />
        </Tooltip>
      </div>
      <div style={{ padding: "10px" }}>
        <div>
          <FroalaTextEditor
            editorHeight={"80px"}
            name={"text_body_1"}
            id={item.id}
            value={item?.title}
            onChange={(e) => handleTitleChange(item?.id, e)}
            handleEditorCustomization={() => {}}
            editorRef={editorRef1}
            label={"title"}
            toolbarButtons={[
              "fontFamily",
              "fontSize",
              "textColorCustom",
              "bold",
              "italic",
              "underline",
            ]}
            type="title"
            characters="35"
          />
        </div>

        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
              marginTop: "10px",
            }}
          >
            <TextView
              style={{
                fontFamily: "DM Sans",
                fontSize: "16px",
                fontWeight: "500",
                color: colors.primary,
                marginBottom: "10px",
                marginTop: "15px",
              }}
            >
              Button Link
            </TextView>
            <OpenInNewIcon
              style={{
                cursor: linkError ? "not-allowed" : "pointer",
                color: linkError ? "#BBBBBB" : "#9B5AF8",
                fontSize: "20px",
              }}
              onClick={() => {
                const raw = stripHtml(item?.link);
                if (raw && !linkError) goToUrl(raw);
              }}
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gridGap: 1,
            }}
          >
            <div style={{ maxWidth: "25vw" }}>
              <FroalaTextEditor
                editorHeight={"65px"}
                name={"text_body_2"}
                id={item?.id}
                value={item?.link}
                onChange={(e) => {
                  handleLinkChange(item?.id, e);
                }}
                handleEditorCustomization={() => {}}
                editorRef={editorRef2}
                label={""}
                toolbarButtons={[]}
                type="title"
                placeholder="www.something.com"
                trimUrls={true}
                // characters="25"
              />
            </div>
          </div>
          {linkError && (
            <TextView
              style={{
                fontSize: "12px",
                color: "#B5251F",
                marginTop: "6px",
              }}
            >
              Enter a full link (https://…) or leave it empty.
            </TextView>
          )}
        </div>

        <SettingsSlider
          title="Corner Rounding"
          sliderValue={item?.radius}
          onValueChange={(value) => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id ? { ...button, radius: value } : button
              )
            );
            dispatch(updateButtonProps({ radius: value }));
          }}
        />

        <ColorPopup
          title={"Color"}
          background={item?.backgroundColor}
          handleChangeBackgroundColor={(value) => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id
                  ? { ...button, backgroundColor: value }
                  : button
              )
            );
            dispatch(updateButtonProps({ backgroundColor: value }));
          }}
          resetBackgroundColor={() => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id
                  ? { ...button, backgroundColor: "" }
                  : button
              )
            );
            dispatch(updateButtonProps({ backgroundColor: "" }));
          }}
          sliderValue={item?.opacity}
          sliderChange={(value) => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id ? { ...button, opacity: value } : button
              )
            );
            dispatch(updateButtonProps({ opacity: value }));
          }}
          sliderBoxTitle={"Transparency"}
          showSuggestedColorPalette={true}
        />

        <div style={{ marginTop: "10px" }}></div>
        <ColorPopup
          title={"Border"}
          background={item?.border.color}
          handleChangeBackgroundColor={(value) => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id
                  ? { ...button, border: { ...button.border, color: value } }
                  : button
              )
            );
            dispatch(
              updateButtonProps({
                border: { ...buttonProps.border, color: value },
              })
            );
          }}
          resetBackgroundColor={() => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id
                  ? { ...button, border: { ...button.border, color: "" } }
                  : button
              )
            );
            dispatch(
              updateButtonProps({
                border: { ...buttonProps.border, color: "" },
              })
            );
          }}
          sliderValue={item?.border.width}
          sliderChange={(value) => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id
                  ? { ...button, border: { ...button.border, width: value } }
                  : button
              )
            );
            dispatch(
              updateButtonProps({
                border: { ...buttonProps.border, width: value },
              })
            );
          }}
          sliderBoxTitle={"Border Width"}
          sliderMaxValue={8}
          sliderMinValue={0}
          sliderSymbol={"px"}
          showSuggestedColorPalette={true}
          isBorderSlider
        />
        <div style={{ marginTop: "10px" }}></div>
        <ColorPopup
          title="Drop Shadow"
          background={item?.dropShadowColor}
          handleChangeBackgroundColor={(value) => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id
                  ? { ...button, dropShadowColor: value }
                  : button
              )
            );
            dispatch(updateButtonProps({ dropShadowColor: value }));
          }}
          resetBackgroundColor={() => {
            setList((prevList) =>
              prevList.map((button) =>
                button.id === item.id
                  ? { ...button, dropShadowColor: "" }
                  : button
              )
            );
            dispatch(updateButtonProps({ dropShadowColor: "" }));
          }}
          showSlider={false}
          isHideGradient={true}
        />
      </div>
    </div>
  );
}

export default ButtonsDesign;
